import { useState } from "react";
import { EonUiButton, EonUiInput } from "@eon-ui/eon-ui-components-react";
import { useJwtToken } from "@me8eon/react_authentication";
import { api } from "../expertApi";
import type { ItemDetail } from "../aidwops.types";
import "./AidwopsAiAssistantSslBypassRequestResponse.scss";

// Export / alias EonUiTextInput for semantic clarity
export const EonUiTextInput = EonUiInput;

export interface AidwopsAiAssistantSslBypassRequestResponseProps {
    url?: string;
    onCancel?: () => void;
    onOpenExpertQueue: () => void;
}

export function AidwopsAiAssistantSslBypassRequestResponse({
    url,
    onCancel,
    onOpenExpertQueue,
}: AidwopsAiAssistantSslBypassRequestResponseProps) {
    const jwtToken = useJwtToken();
    const [stage, setStage] = useState<"submit" | "success" | "failure">(
        "submit"
    );
    const [targetUrl, setTargetUrl] = useState(url ?? "");
    const [justification, setJustification] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCancelled, setIsCancelled] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [result, setResult] = useState<ItemDetail | null>(null);

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        } else {
            setIsCancelled(true);
        }
    };

    const handleSubmit = async () => {
        if (!targetUrl.trim() || !justification.trim() || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            const accessToken = await jwtToken();
            if (!accessToken) {
                throw new Error(
                    "Unable to acquire access token for expert API"
                );
            }

            const res = await api.raiseSslBypassRequest(
                targetUrl.trim(),
                justification.trim(),
                accessToken
            );
            setResult(res);
            setStage("success");
        } catch (err) {
            const rawMessage = err instanceof Error ? err.message : String(err);
            const cleanedMessage = rawMessage.replace(
                /^raiseSslBypassRequest failed(?:\s*\(\d+\))?:\s*/i,
                ""
            );
            setErrorMessage(cleanedMessage);
            setStage("failure");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isCancelled) {
        return (
            <div className="aidwops-ssl-bypass-card aidwops-ssl-bypass-card--cancelled">
                <p>SSL bypass request cancelled.</p>
                <EonUiButton
                    rank="secondary"
                    size="small"
                    text="Reopen"
                    onClick={() => setIsCancelled(false)}
                />
            </div>
        );
    }

    if (stage === "success") {
        const tickets = result?.tickets ?? [];
        const ticketKeys = tickets
            .map((t) => t.key)
            .filter((key): key is string => Boolean(key && key.trim()));

        const displayRequestId = result?.id
            ? ticketKeys.length > 0 && !ticketKeys.includes(result.id)
                ? `${result.id} / ${ticketKeys.join(" / ")}`
                : result.id
            : ticketKeys.join(" / ");

        const displayStatus =
            result?.statusLabel ||
            (result?.status
                ? result.status
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())
                : "Awaiting review");

        return (
            <div
                className="aidwops-ssl-bypass-card"
                role="region"
                aria-label="SSL bypass request status"
            >
                <div className="aidwops-ssl-bypass-card__success-header">
                    <span
                        className="aidwops-ssl-bypass-card__success-icon"
                        aria-hidden="true"
                    >
                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#137333"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <path d="m9 12 2 2 4-4" />
                        </svg>
                    </span>
                    <div className="aidwops-ssl-bypass-card__header-text">
                        <h4 className="aidwops-ssl-bypass-card__title">
                            {targetUrl}
                        </h4>
                        <span className="aidwops-ssl-bypass-card__success-subtitle">
                            Request sent to Expert Queue
                        </span>
                    </div>
                </div>

                <p className="aidwops-ssl-bypass-card__description">
                    Network security will review your justification and approve
                    or deny the SSL bypass.
                </p>

                <div className="aidwops-ssl-bypass-card__summary-box">
                    {displayRequestId && (
                        <div className="aidwops-ssl-bypass-card__summary-row">
                            <span className="aidwops-ssl-bypass-card__summary-label">
                                Request ID:
                            </span>
                            <span className="aidwops-ssl-bypass-card__summary-value">
                                {displayRequestId}
                            </span>
                        </div>
                    )}
                    <div className="aidwops-ssl-bypass-card__summary-row">
                        <span className="aidwops-ssl-bypass-card__summary-label">
                            Status
                        </span>
                        <span className="aidwops-ssl-bypass-card__status-badge">
                            {displayStatus}
                        </span>
                    </div>
                </div>

                {tickets.length > 0 && (
                    <div className="aidwops-ssl-bypass-card__tickets">
                        {tickets.map((ticket, index) => {
                            const systemLower =
                                ticket.system?.toLowerCase() ?? "";
                            const isJira = systemLower === "jira";
                            const isServiceNow = systemLower === "servicenow";

                            return (
                                <div
                                    key={ticket.key || index}
                                    className="aidwops-ssl-bypass-card__ticket-row"
                                >
                                    <div className="aidwops-ssl-bypass-card__ticket-brand">
                                        {isJira ? (
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    fill="#2684FF"
                                                    d="M12.006 1.998c0 4.418-3.582 8-8 8v3.998c6.627 0 12-5.373 12-12h-4z"
                                                />
                                                <path
                                                    fill="#0052CC"
                                                    d="M1.998 12.006c4.418 0 8 3.582 8 8h3.998c0-6.627-5.373-12-12-12v4z"
                                                />
                                            </svg>
                                        ) : isServiceNow ? (
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="8.5"
                                                    stroke="#60bc53"
                                                    strokeWidth="2.5"
                                                    fill="none"
                                                />
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="3.5"
                                                    fill="#60bc53"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="#4b5563"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                aria-hidden="true"
                                            >
                                                <rect
                                                    x="3"
                                                    y="4"
                                                    width="18"
                                                    height="16"
                                                    rx="2"
                                                />
                                                <line
                                                    x1="7"
                                                    y1="8"
                                                    x2="17"
                                                    y2="8"
                                                />
                                                <line
                                                    x1="7"
                                                    y1="12"
                                                    x2="17"
                                                    y2="12"
                                                />
                                            </svg>
                                        )}
                                        <span className="aidwops-ssl-bypass-card__ticket-system">
                                            {isJira
                                                ? "JIRA"
                                                : isServiceNow
                                                  ? "ServiceNow"
                                                  : ticket.system || "Ticket"}
                                        </span>
                                    </div>
                                    <span className="aidwops-ssl-bypass-card__ticket-key">
                                        {ticket.key}
                                    </span>
                                    {ticket.url ? (
                                        <a
                                            href={ticket.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="aidwops-ssl-bypass-card__ticket-open"
                                        >
                                            Open
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                aria-hidden="true"
                                            >
                                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                                <polyline points="15 3 21 3 21 9" />
                                                <line
                                                    x1="10"
                                                    y1="14"
                                                    x2="21"
                                                    y2="3"
                                                />
                                            </svg>
                                        </a>
                                    ) : ticket.status ? (
                                        <span className="aidwops-ssl-bypass-card__ticket-status">
                                            {ticket.status}
                                        </span>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                )}

                <EonUiButton
                    rank="primary"
                    text="Open Expert Queue"
                    disabled={isSubmitting}
                    preloader={isSubmitting}
                    onClick={onOpenExpertQueue}
                    size="small"
                />
            </div>
        );
    }

    if (stage === "failure") {
        return (
            <div
                className="aidwops-ssl-bypass-card"
                role="region"
                aria-label="SSL bypass request failure"
            >
                <div className="aidwops-ssl-bypass-card__failure-header">
                    <span
                        className="aidwops-ssl-bypass-card__failure-icon"
                        aria-hidden="true"
                    >
                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#cf222e"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                    </span>
                    <div className="aidwops-ssl-bypass-card__header-text">
                        <h4 className="aidwops-ssl-bypass-card__title">
                            {targetUrl}
                        </h4>
                        <span className="aidwops-ssl-bypass-card__failure-subtitle">
                            Request could not be sent to Expert Queue
                        </span>
                    </div>
                </div>

                <p className="aidwops-ssl-bypass-card__description">
                    Network security service encountered an error while
                    processing your request.
                </p>

                <div
                    className="aidwops-ssl-bypass-card__error-box"
                    role="alert"
                >
                    {errorMessage ||
                        "The SSL bypass request could not be completed. Please try again or contact the security team."}
                </div>

                <div className="aidwops-ssl-bypass-card__actions">
                    <EonUiButton
                        rank="secondary"
                        text="Cancel"
                        onClick={handleCancel}
                        size="small"
                    />
                    <EonUiButton
                        rank="primary"
                        text="Try Again"
                        onClick={() => setStage("submit")}
                        size="small"
                    />
                </div>
            </div>
        );
    }

    // Default stage: "submit"
    return (
        <div
            className="aidwops-ssl-bypass-card"
            role="region"
            aria-label="SSL bypass request form"
        >
            <div className="aidwops-ssl-bypass-card__category">
                SSL bypass request
            </div>
            <h4 className="aidwops-ssl-bypass-card__title">
                {targetUrl || "New SSL bypass request"}
            </h4>

            <form
                className="aidwops-ssl-bypass-card__form"
                onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                }}
            >
                <div className="aidwops-ssl-bypass-card__field">
                    <EonUiTextInput
                        name="ssl-bypass-url"
                        label="URL to bypass SSL inspection for"
                        value={targetUrl}
                        disabled={isSubmitting}
                        onValueChanged={(e: any) =>
                            setTargetUrl(e.target?.value ?? "")
                        }
                    />
                </div>

                <div className="aidwops-ssl-bypass-card__field">
                    <EonUiTextInput
                        name="ssl-bypass-justification"
                        label="Business justification"
                        textarea
                        rows={4}
                        placeholder="Why does SSL inspection need to be bypassed for this URL? Include project, team, or approval reference if available."
                        value={justification}
                        disabled={isSubmitting}
                        onValueChanged={(e: any) =>
                            setJustification(e.target?.value ?? "")
                        }
                    />
                </div>

                <div className="aidwops-ssl-bypass-card__actions">
                    <EonUiButton
                        rank="secondary"
                        text="Cancel"
                        disabled={isSubmitting}
                        onClick={handleCancel}
                        size="small"
                    />
                    <EonUiButton
                        rank="primary"
                        text="Submit Request"
                        disabled={
                            isSubmitting ||
                            !targetUrl.trim() ||
                            !justification.trim()
                        }
                        preloader={isSubmitting}
                        onClick={handleSubmit}
                        size="small"
                    />
                </div>
            </form>
        </div>
    );
}

export default AidwopsAiAssistantSslBypassRequestResponse;
